import { Align, Button, Card, Icon, Justify, Row, Size } from '@busybusy/webapp-react-ui';
import {
  ApiKeysIcon,
  BulletinIcon,
  DailyReportsOutlinedIcon,
  DocumentsIcon,
  ListIcon,
  ProgressOutlinedIcon,
} from 'assets/icons';
import classNames from 'classnames';
import ConfirmationDialog from 'components/foundation/dialogs/ConfirmationDialog/ConfirmationDialog';
import HeaderDialog from 'components/foundation/dialogs/HeaderDialog/HeaderDialog';
import { useOpenable } from 'hooks';
import { t } from 'i18next';
import { useFeatureFlags } from 'utils/features';
import './EnablePremiumDialog.scss';
import EnablePremiumForm from './EnablePremiumForm/EnablePremiumForm';

export interface IEnablePremiumDialogProps {
  className?: string | object;
}

export const EnablePremiumDialog = (props: IEnablePremiumDialogProps) => {
  const { className } = props;
  const classes = classNames(className, 'enable-premium-dialog', 'my-10');
  const isPro = useFeatureFlags('PRO');

  const premiumDialogOpenable = useOpenable();
  const premiumRequestCompleteOpenable = useOpenable();

  const onPremiumRequestSubmitted = () => {
    premiumRequestCompleteOpenable.open();
    premiumDialogOpenable.close();
  };

  return (
    <>
      <Card className={classes}>
        <div className={'premium-bundle p-6'}>
          <Row justify={Justify.CENTER} className="mt-6">
            <h2 className="subheading">{t('PREMIUM BUNDLE')}</h2>
          </Row>
          <Row justify={Justify.CENTER}>
            <div className="separator my-4"></div>
          </Row>
          <Row justify={Justify.CENTER} align={Align.CENTER} className={'feature-text my-6 mx-12'}>
            {t(
              'Looking to take your business to the next level and save big money at the same time? The Premium bundle gives you access to all the Premium features for only an additional monthly fee of $5 per employee.'
            )}
          </Row>
          <Row justify={Justify.CENTER} align={Align.CENTER}>
            <Card className={'feature-container'}>
              <Row justify={Justify.CENTER}>
                <Icon svg={ListIcon} size={Size.XL} />
              </Row>
              <Row justify={Justify.CENTER} className={'feature-heading'}>
                {t('All Pro Features')}
              </Row>
            </Card>
            <Card className={'feature-container'}>
              <Row justify={Justify.CENTER}>
                <Icon svg={ProgressOutlinedIcon} size={Size.XL} />
              </Row>
              <Row justify={Justify.CENTER} className={'feature-heading'}>
                {t('Progress Tracking')}
              </Row>
            </Card>
            <Card className={'feature-container'}>
              <Row justify={Justify.CENTER}>
                <Icon svg={DailyReportsOutlinedIcon} size={Size.XL} />
              </Row>
              <Row justify={Justify.CENTER} className={'feature-heading'}>
                {t('Daily Project Reports')}
              </Row>
            </Card>
            <Card className={'feature-container'}>
              <Row justify={Justify.CENTER}>
                <Icon svg={BulletinIcon} size={Size.XL} />
              </Row>
              <Row justify={Justify.CENTER} className={'feature-heading'}>
                {t('Company Bulletin')}
              </Row>
            </Card>
            <Card className={'feature-container'}>
              <Row justify={Justify.CENTER}>
                <Icon svg={DocumentsIcon} size={Size.XL} />
              </Row>
              <Row justify={Justify.CENTER} className={'feature-heading'}>
                {t('Document Sharing')}
              </Row>
            </Card>
            <Card className={'feature-container'}>
              <Row justify={Justify.CENTER}>
                <Icon svg={ApiKeysIcon} size={Size.XL} />
              </Row>
              <Row justify={Justify.CENTER} className={'feature-heading'}>
                {t('API Keys')}
              </Row>
            </Card>
          </Row>
          <Row justify={Justify.CENTER}>
            <Button
              className="go-premium-button my-8"
              type="primary"
              onClick={
                isPro
                  ? premiumDialogOpenable.open
                  : () => window.open('https://calendly.com/busybusy-walkthrough/20min')
              }
              size={Size.LARGE}
            >
              {t('Go Premium')}
            </Button>
          </Row>
        </div>
        <div className="payroll-services p-6">
          <Row justify={Justify.CENTER} className="mt-6">
            <h2 className="subheading">{t('PAYROLL SERVICES')}</h2>
          </Row>
          <Row justify={Justify.CENTER}>
            <div className="separator my-4"></div>
          </Row>
          <Row justify={Justify.CENTER} align={Align.CENTER} className={'feature-text my-6 mx-12'}>
            {t(
              'Want to be able to process payroll and instantly send out checks with the press of a button? Pricing is only $45 per month, plus $6 per month for each employee. Talk to a busybusy representative about getting started with Busy Payroll.'
            )}
          </Row>
          <Row justify={Justify.CENTER}>
            <Button
              className="schedule-button my-8"
              type="link"
              onClick={() => window.open('https://calendly.com/busypayroll/demo')}
              size={Size.LARGE}
            >
              {t('Schedule Appointment')}
            </Button>
          </Row>
        </div>
      </Card>

      <HeaderDialog
        isOpen={premiumDialogOpenable.isOpen}
        onClose={premiumDialogOpenable.close}
        title={t('Premium')}
        divider={false}
      >
        <EnablePremiumForm onRequestSubmitted={onPremiumRequestSubmitted} className="pl-8 pr-8 pb-9" />
      </HeaderDialog>
      <ConfirmationDialog
        title={t('Submitted')}
        message={t(
          'Your request has been submitted and Premium will soon be enabled for your account. We’ll send you a message letting you know when the feature is ready to use. Thank you!'
        )}
        isOpen={premiumRequestCompleteOpenable.isOpen}
        confirmButtonText={t('OK')}
        onConfirm={premiumRequestCompleteOpenable.close}
      />
    </>
  );
};
